<template>
	<!-- Pressing a button keeps the cursor in the row's field. -->
	<div
		class="row-format-bar"
		role="toolbar"
		:aria-label="label"
		@pointerdown.prevent
		@mousedown.prevent
	>
		<button
			type="button"
			class="row-format-btn row-format-btn--bold"
			:class="{ active: row.bold }"
			:aria-pressed="row.bold ? 'true' : 'false'"
			aria-label="Bold"
			title="Bold"
			@click="emit('toggle', 'bold')"
		>B</button>
		<button
			type="button"
			class="row-format-btn row-format-btn--italic"
			:class="{ active: row.italic }"
			:aria-pressed="row.italic ? 'true' : 'false'"
			aria-label="Italic"
			title="Italic"
			@click="emit('toggle', 'italic')"
		>I</button>
		<span class="row-format-divider" aria-hidden="true"></span>
		<button
			type="button"
			class="row-format-swatch row-format-swatch--black"
			:class="{ active: row.color !== 'blue' }"
			:aria-pressed="row.color !== 'blue' ? 'true' : 'false'"
			aria-label="Black text"
			title="Black"
			@click="emit('color', null)"
		></button>
		<button
			type="button"
			class="row-format-swatch row-format-swatch--blue"
			:class="{ active: row.color === 'blue' }"
			:aria-pressed="row.color === 'blue' ? 'true' : 'false'"
			aria-label="Blue text"
			title="Blue"
			@click="emit('color', 'blue')"
		></button>
		<template v-if="blockToggle">
			<span class="row-format-divider" aria-hidden="true"></span>
			<button
				type="button"
				class="row-format-btn row-format-btn--block"
				:class="{ active: row.block }"
				:aria-pressed="row.block ? 'true' : 'false'"
				title="Price this row as a block (building)"
				@click="emit('toggle', 'block')"
			>Block</button>
			<button
				type="button"
				class="row-format-btn row-format-btn--block"
				:class="{ active: row.total }"
				:aria-pressed="row.total ? 'true' : 'false'"
				title="Show the hours of the block rows added up in this row"
				@click="emit('toggle', 'total')"
			>Total</button>
		</template>
	</div>
</template>

<script setup>
// The format bar over a document table row being edited: bold, italic, black or blue.
defineProps({
	// The row's current format: { bold, italic, color }.
	row: { type: Object, required: true },
	label: { type: String, default: 'Format row' },
	// Proposals can mark schedule rows as blocks (buildings priced on their own) and one row as
	// their total.
	blockToggle: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'color']);
</script>

<style scoped>
.row-format-bar {
	position: absolute;
	left: 4px;
	bottom: calc(100% + 3px);
	z-index: 6;
	display: inline-flex;
	align-items: center;
	gap: 2px;
	padding: 2px;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}

.row-format-btn,
.row-format-swatch {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	padding: 0;
	border: 0;
	border-radius: 6px;
	background: transparent;
	color: var(--text-color);
	font: 700 14px/1 var(--font-family-base);
	cursor: pointer;
	touch-action: manipulation;
}

.row-format-btn--italic {
	font: italic 500 15px/1 Georgia, 'Times New Roman', serif;
}

.row-format-btn--block {
	width: auto;
	padding: 0 8px;
	font: 600 12px/1 var(--font-family-base);
}

.row-format-btn.active {
	background: var(--noc-blue-10);
	color: var(--noc-blue);
}

.row-format-divider {
	width: 1px;
	height: 18px;
	margin: 0 3px;
	background: var(--border-color);
}

.row-format-swatch::before {
	content: '';
	width: 14px;
	height: 14px;
	border-radius: 50%;
	background: currentColor;
}

.row-format-swatch.active::before {
	box-shadow: 0 0 0 2px var(--noc-background, #fff), 0 0 0 3.5px currentColor;
}

.row-format-swatch--black {
	color: #1f1f1f;
}

/* The print blue of the generated documents (CLIENT_DOCUMENT_BLUE). */
.row-format-swatch--blue {
	color: #1f4e79;
}

@media (hover: hover) {
	.row-format-btn:hover:not(.active),
	.row-format-swatch:hover {
		background: var(--noc-background-secondary);
	}
}
</style>
